import { useState, useEffect, useCallback } from 'react';
import { FileText, Download, CheckSquare, Square, AlertCircle, CheckCircle2, Receipt } from 'lucide-react';
import type { Transaction, Account } from '../types/financeV2.types';
import { financeV2Service } from '../services/financeV2Service';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import { AmountText } from '@/components/finance-v2/FinanceBadges';
import { cn } from '@/utils/cn';

import { getErrorMessage } from '../utils/error.utils'
import { parseDbTimestamp } from '../utils/datetime'

function EmptyCard({ title, description }: { title: string; description: string }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
        <FileText className="size-10 text-muted-foreground" aria-hidden="true" />
        <h3 className="text-sm font-medium">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  );
}

export function FinanceV2CarneGenerationPage() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedTransactions, setSelectedTransactions] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [carneUrl, setCarneUrl] = useState<string | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [recurrenceGroupFilter] = useState<string>('');

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    let firstAccountId = '';
    try {
      const response = await financeV2Service.accounts.list({ isActive: true });
      const btgAccounts = response.data.accounts.filter(acc => acc.isBtgEnabled);
      setAccounts(btgAccounts);
      firstAccountId = btgAccounts[0]?.id ?? '';
      if (firstAccountId) {
        setSelectedAccount(firstAccountId);
      }
    } catch (err: unknown) {
      setError('Erro ao carregar contas');
    } finally {
      // Sem conta BTG não há transações a carregar: encerra o carregamento
      // para exibir o aviso em vez do esqueleto infinito.
      if (!firstAccountId) {
        setLoading(false);
      }
    }
  };

  const loadTransactions = useCallback(async () => {
    if (!selectedAccount) return;

    try {
      setLoading(true);
      setError(null);

      // Load all transactions with boletos for the selected account
      const response = await financeV2Service.transactions.list({
        accountId: selectedAccount,
        status: 'OPEN',
        limit: 100
      });

      // Filter only transactions that have boletos
      const transactionsWithBoletos = response.data.transactions.filter(
        t => (t.metadata as { boleto?: { id?: string } } | undefined)?.boleto?.id
      );

      setTransactions(transactionsWithBoletos);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao carregar transações'));
    } finally {
      setLoading(false);
    }
  }, [selectedAccount]);

  useEffect(() => {
    if (selectedAccount) {
      loadTransactions();
    }
  }, [selectedAccount, loadTransactions]);

  const toggleTransaction = (transactionId: string) => {
    const newSelected = new Set(selectedTransactions);
    if (newSelected.has(transactionId)) {
      newSelected.delete(transactionId);
    } else {
      newSelected.add(transactionId);
    }
    setSelectedTransactions(newSelected);
  };

  const selectAllFromRecurrenceGroup = (groupId: string) => {
    const groupTransactions = transactions.filter(t => t.recurrenceGroupId === groupId);
    const newSelected = new Set(selectedTransactions);
    groupTransactions.forEach(t => newSelected.add(t.id));
    setSelectedTransactions(newSelected);
  };

  const clearSelection = () => {
    setSelectedTransactions(new Set());
  };

  const handleGenerateCarne = async () => {
    if (selectedTransactions.size === 0) {
      setError('Selecione pelo menos uma transação');
      return;
    }

    if (!selectedAccount) {
      setError('Selecione uma conta');
      return;
    }

    setGenerating(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await financeV2Service.btg.generateCarne({
        accountId: selectedAccount,
        transactionIds: Array.from(selectedTransactions)
      });

      setCarneUrl(response.data.carne.carneUrl);
      setSuccess(`Carnê gerado com sucesso! ${response.data.carne.boletos.length} boletos incluídos.`);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao gerar carnê'));
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadCarne = () => {
    if (carneUrl) {
      window.open(carneUrl, '_blank');
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return parseDbTimestamp(dateString)?.toLocaleDateString('pt-BR') ?? '-';
  };

  // Group transactions by recurrence group
  const transactionsByRecurrenceGroup = transactions.reduce((acc, transaction) => {
    const groupId = transaction.recurrenceGroupId || 'single';
    if (!acc[groupId]) {
      acc[groupId] = [];
    }
    acc[groupId].push(transaction);
    return acc;
  }, {} as Record<string, Transaction[]>);

  const filteredGroups = recurrenceGroupFilter
    ? Object.entries(transactionsByRecurrenceGroup).filter(([groupId]) =>
        groupId === recurrenceGroupFilter
      )
    : Object.entries(transactionsByRecurrenceGroup);

  const totalSelectedAmount = transactions
    .filter(t => selectedTransactions.has(t.id))
    .reduce((sum, t) => sum + t.amount, 0);

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex items-center gap-2">
          <Receipt className="size-6 shrink-0 text-muted-foreground" />
          <h1 className="text-2xl font-semibold">Geração de Carnê</h1>
        </div>
        <p className="text-sm text-muted-foreground">Selecione transações com boletos para gerar um carnê (PDF único)</p>
      </div>

      <Card size="sm">
        <CardContent className="flex flex-col gap-1.5">
          <Label htmlFor="carne-account">Conta Bancária (BTG Habilitado)</Label>
          <NativeSelect
            id="carne-account"
            value={selectedAccount}
            onChange={(e) => {
              setSelectedAccount(e.target.value);
              clearSelection();
              setCarneUrl(null);
              setSuccess(null);
            }}
          >
            <option value="">Selecione uma conta...</option>
            {accounts.map(account => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </NativeSelect>

          {accounts.length === 0 && !loading && (
            <p className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="size-4 shrink-0" aria-hidden="true" />
              Nenhuma conta com integração BTG habilitada encontrada
            </p>
          )}
        </CardContent>
      </Card>

      {error && (
        <div role="alert" className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden="true" />
          <p className="text-destructive">{error}</p>
        </div>
      )}

      {success && (
        <div role="status" className="flex flex-col gap-3 rounded-lg border bg-muted/40 p-3 text-sm sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-2">
            <CheckCircle2 className="size-4 shrink-0 text-primary" aria-hidden="true" />
            <span className="break-words">{success}</span>
          </div>
          {carneUrl && (
            <Button onClick={handleDownloadCarne} className="self-start sm:self-auto">
              <Download className="size-4" />
              Baixar Carnê PDF
            </Button>
          )}
        </div>
      )}

      {selectedTransactions.size > 0 && (
        <Card size="sm">
          <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-sm font-medium">
                {selectedTransactions.size} {selectedTransactions.size === 1 ? 'transação selecionada' : 'transações selecionadas'}
              </div>
              <div className="mt-1 text-sm text-muted-foreground">
                Valor total: <AmountText tone="neutral" className="font-semibold">{formatCurrency(totalSelectedAmount)}</AmountText>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={clearSelection}>
                Limpar Seleção
              </Button>
              <Button onClick={handleGenerateCarne} loading={generating}>
                {!generating && <FileText className="size-4" />}
                {generating ? 'Gerando...' : 'Gerar Carnê'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex flex-col gap-2" aria-busy="true">
          <span className="sr-only">Carregando transações...</span>
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-20" />)}
        </div>
      ) : !selectedAccount ? (
        <EmptyCard
          title="Selecione uma conta"
          description="Escolha uma conta bancária com BTG habilitado para começar."
        />
      ) : transactions.length === 0 ? (
        <EmptyCard
          title="Nenhuma transação com boleto encontrada"
          description="Crie transações com boletos gerados para poder gerar carnês."
        />
      ) : (
        <div className="flex flex-col gap-4">
          {filteredGroups.map(([groupId, groupTransactions]) => {
            const isRecurringGroup = groupId !== 'single';

            return (
              <Card key={groupId} className="min-w-0 gap-0 py-0">
                {isRecurringGroup && (
                  <div className="flex flex-col gap-2 border-b bg-muted/40 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <div className="break-words text-sm font-semibold">
                        Grupo Recorrente - {groupTransactions[0]?.description}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {groupTransactions.length} parcelas • Valor total: {formatCurrency(
                          groupTransactions.reduce((sum, t) => sum + t.amount, 0)
                        )}
                      </div>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => selectAllFromRecurrenceGroup(groupId)}
                      className="self-start sm:self-auto"
                    >
                      Selecionar Todas
                    </Button>
                  </div>
                )}

                <div className="divide-y divide-border">
                  {groupTransactions.map((transaction) => {
                    const isSelected = selectedTransactions.has(transaction.id);
                    const boleto = (transaction.metadata as { boleto?: { id?: string; digitableLine?: string; status?: string } } | undefined)?.boleto;

                    return (
                      <div
                        key={transaction.id}
                        onClick={() => toggleTransaction(transaction.id)}
                        className={cn(
                          'cursor-pointer px-4 py-4 transition-colors hover:bg-muted',
                          isSelected && 'bg-accent'
                        )}
                      >
                        <div className="flex items-start gap-4">
                          <div className="shrink-0 pt-1">
                            {isSelected ? (
                              <CheckSquare className="size-5 text-primary" aria-hidden="true" />
                            ) : (
                              <Square className="size-5 text-muted-foreground" aria-hidden="true" />
                            )}
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                              <div className="min-w-0">
                                <div className="break-words text-sm font-medium">
                                  {transaction.description}
                                </div>
                                {transaction.installmentNumber && (
                                  <div className="mt-1 text-xs text-muted-foreground">
                                    Parcela {transaction.installmentNumber}/{transaction.totalInstallments}
                                  </div>
                                )}
                              </div>
                              <AmountText tone="neutral" className="shrink-0 text-sm font-semibold sm:ml-4">
                                {formatCurrency(transaction.amount)}
                              </AmountText>
                            </div>

                            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground sm:gap-4">
                              <div>
                                Vencimento: <span className="font-medium tabular-nums">{formatDate(transaction.dueDate)}</span>
                              </div>
                              {boleto?.digitableLine && (
                                <div className="break-all font-mono text-xs">
                                  Linha digitável: {boleto.digitableLine.substring(0, 15)}...
                                </div>
                              )}
                            </div>

                            {boleto && (
                              <div className="mt-2 flex flex-wrap items-center gap-2">
                                <Badge variant="secondary">Boleto Gerado</Badge>
                                {boleto.status && (
                                  <span className="text-xs text-muted-foreground">
                                    Status: {boleto.status}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {!loading && selectedAccount && transactions.length > 0 && selectedTransactions.size === 0 && (
        <div className="flex flex-col items-center gap-2 rounded-lg border-2 border-dashed border-border py-8 text-center">
          <CheckSquare className="size-10 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">
            Clique nas transações acima para selecioná-las e gerar o carnê
          </p>
        </div>
      )}
    </div>
  );
}
